import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from "react";
import { navigate } from "gatsby";

interface NavigationContextType {
  isNavigating: boolean;
  navigatingTo: string | null;
  navigateTo: (path: string, currentPath: string) => void;
  resetNavigation: () => void;
}

const NavigationContext = createContext<NavigationContextType>({
  isNavigating: false,
  navigatingTo: null,
  navigateTo: () => {},
  resetNavigation: () => {},
});

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigatingTo, setNavigatingTo] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetNavigation = useCallback(() => {
    setIsNavigating(false);
    setNavigatingTo(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const navigateTo = useCallback((path: string, currentPath: string) => {
    if (path === currentPath) return;
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Start loading animation
    setIsNavigating(true);
    setNavigatingTo(path);
    
    // Navigate immediately
    navigate(path);
    
    // Reset state after short delay to allow page transition
    timeoutRef.current = setTimeout(() => {
      setIsNavigating(false);
      setNavigatingTo(null);
    }, 500);
  }, []);

  return (
    <NavigationContext.Provider value={{ isNavigating, navigatingTo, navigateTo, resetNavigation }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextType => {
  return useContext(NavigationContext);
};



